import { useCustom, useTranslate } from '@refinedev/core';
import { Alert, Button, Table, Tag, Tooltip, Typography } from 'antd';
import { useState } from 'react';
import { ResultCard, fmtDay, type ToolLocale } from '../../components/ToolSection';
import { ChooseWayDrawer, DescribeWayDrawer, RateWayDrawer } from './RoseDrawers';
import {
  ROSE_CENTRE,
  ROSE_ENOUGH,
  ROSE_FIELDS,
  ROSE_NEEDS,
  ROSE_RADIUS,
  ROSE_SIZE,
  meanText,
  roseAngle,
  roseNextStep,
  rosePolygon,
  type RoseRow,
  type RoseStrategy,
} from './roseModel';

const WAY_COLORS = ['#7b5a36', '#5c6145', '#2f6f73', '#8a3b3b'];
/** With this many ways or fewer every way's details are open on load (they are results, not extras). */
const OPEN_ALL_UP_TO = 2;

/**
 * E53 · WindRose (portal: "Client Hub — Values — Wind rose", with "… — Over time (section)") and its in-session acts — the client's
 * ways of living on the rose: the eight raw scores on the eight rays, the ring at 8 marked as enough, the mean capped at 8 beside it —
 * the choice (value, beliefs, people who share it), the written plan and the ratings over time (does the new way pay off?). READ-ONLY for
 * what the client did; the therapist can ALSO describe, rate and choose WITH the client (Q-e53-2 B), each as a side drawer opened from
 * this card: the same commands as the phone's, `performer` = the therapist (the server's), the practice's record, no homework completed.
 * The three buttons are always there, in order; the next step is primary, one that cannot act yet is disabled and says why.
 * A technique by Dmitri Dyakov. (Design PORTAL-SECTIONS-2026-10-22 §2.4 V7–V9.)
 */
type Props = {
  clientId: string;
  clientName: string;
  locale: ToolLocale;
  /** Called after a saved in-session act (the profile above shows the chosen way). */
  onChanged?: () => void;
};

type Drawer = 'describe' | 'rate' | 'choose' | null;

export const WindRoseHub = ({ clientId, clientName, locale, onChanged }: Props) => {
  const t = useTranslate() as unknown as (k: string, o?: Record<string, unknown>) => string;
  const query = useCustom<RoseRow>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'windRose',
      fields: ROSE_FIELDS,
      variables: { subject: { name: 'subject', type: 'ID', value: clientId } },
    },
    queryOptions: { enabled: Boolean(clientId), retry: false },
  });
  const rose = query.data?.data ?? null;
  const ways = rose?.strategies ?? [];
  const rated = ways.filter((w) => w.scores);
  const next = roseNextStep(ways);

  const [drawer, setDrawer] = useState<Drawer>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const closeDrawer = () => setDrawer(null);
  const saved = () => {
    query.refetch();
    onChanged?.();
  };

  const historyRows = ways.flatMap((w) =>
    w.history.map((h) => ({ key: h.ratingId, way: w, rating: h })),
  );
  historyRows.sort((a, b) => a.rating.ratedAt.localeCompare(b.rating.ratedAt));

  const basisLabel = (basis: string | null) =>
    basis ? t(`rose.hub.history.basis${basis === 'lived' ? 'Lived' : 'Imagined'}`) : '';

  const stepButton = (
    step: 'describe' | 'rate' | 'choose',
    label: string,
    testId: string,
    disabledReason: string | null,
  ) => {
    const button = (
      <Button
        type={next === step ? 'primary' : 'default'}
        disabled={disabledReason !== null}
        onClick={() => setDrawer(step)}
        data-testid={testId}
      >
        {label}
      </Button>
    );
    return disabledReason ? (
      <Tooltip title={disabledReason}>
        {/* a disabled button swallows hover; the span carries it */}
        <span>{button}</span>
      </Tooltip>
    ) : (
      button
    );
  };

  const isOpen = (w: RoseStrategy) =>
    ways.length <= OPEN_ALL_UP_TO || !!w.chosen || !!expanded[w.strategyId];

  const wayBlock = (w: RoseStrategy, i: number) => {
    const color = WAY_COLORS[i % WAY_COLORS.length];
    const open = isOpen(w);
    const hasDetails = !!(w.scores || w.chosen || w.inspiredBy);
    const examples = w.scores
      ? ROSE_NEEDS.filter((n) => (w.examples as Record<string, string | null> | null)?.[n])
      : [];
    return (
      <div
        key={w.strategyId}
        data-testid={`rose-hub-way-${w.strategyId}`}
        style={{ borderLeft: `3px solid ${color}`, paddingLeft: 12, marginBottom: 12 }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <Typography.Text strong style={{ color }}>
            {w.name}
          </Typography.Text>
          <Tag>{w.kind === 'current' ? t('rose.hub.kindCurrent') : t('rose.hub.kindAlternative')}</Tag>
          {w.chosen ? (
            <Tag color="green" data-testid={`rose-hub-chosen-${w.strategyId}`}>
              {t('rose.hub.chosen')}
            </Tag>
          ) : null}
          {w.describedInSession ? <Tag color="blue">{t('rose.hub.enteredInSession')}</Tag> : null}
        </div>
        {w.behaviors.length ? (
          <div>
            <Typography.Text type="secondary">{w.behaviors.join(' · ')}</Typography.Text>
          </div>
        ) : null}
        {w.scores ? (
          <div>
            <Typography.Text data-testid={`rose-hub-mean-${w.strategyId}`}>
              {t('rose.hub.mean')}: {meanText(w.cappedMean)}
              {w.basis ? ` · ${basisLabel(w.basis)}` : ''}
            </Typography.Text>{' '}
            <Typography.Text type="secondary" data-testid={`rose-hub-raw-mean-${w.strategyId}`}>
              {t('rose.hub.rawMean')}: {meanText(w.rawMean)}
              {w.basis ? ` · ${basisLabel(w.basis)}` : ''}
            </Typography.Text>
          </div>
        ) : null}
        {hasDetails && ways.length > OPEN_ALL_UP_TO && !w.chosen ? (
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => setExpanded({ ...expanded, [w.strategyId]: !expanded[w.strategyId] })}
            data-testid={`rose-hub-details-toggle-${w.strategyId}`}
          >
            {t('rose.hub.details')}
          </Button>
        ) : null}
        {/* always in the DOM (the specs read the scores, choice and plan); collapsed ones are display:none */}
        <div style={{ display: open ? 'block' : 'none' }} data-open={open}>
          {w.inspiredBy ? (
            <div>
              <Typography.Text type="secondary" data-testid={`rose-hub-inspired-${w.strategyId}`}>
                {t('rose.hub.inspired', {
                  kind: t(`rose.describe.inspiredKind.${w.inspiredBy.kind}`),
                  who: w.inspiredBy.ref ? ` — ${w.inspiredBy.ref}` : '',
                })}
              </Typography.Text>
            </div>
          ) : null}
          {w.scores ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 12px' }}>
              {ROSE_NEEDS.map((need, n) => (
                <span key={need} data-testid={`rose-hub-score-${w.strategyId}-${need}`}>
                  {n + 1}. {t(`rose.needs.${need}`)}: <b>{w.scores?.[need]}</b>
                </span>
              ))}
            </div>
          ) : null}
          {examples.length ? (
            <div data-testid={`rose-hub-examples-${w.strategyId}`}>
              {examples.map((need) => (
                <div key={need}>
                  <Typography.Text type="secondary">
                    {t(`rose.needs.${need}`)}: {(w.examples as unknown as Record<string, string>)[need]}
                  </Typography.Text>
                </div>
              ))}
            </div>
          ) : null}
          {w.chosen ? (
            <div data-testid={`rose-hub-choice-${w.strategyId}`} style={{ marginTop: 4 }}>
              <div>
                <Typography.Text strong>
                  {t('rose.hub.chosenValue')}: {w.chosen.value}
                </Typography.Text>
              </div>
              {w.chosen.beliefs.length ? (
                <div>
                  {t('rose.hub.beliefs')}: {w.chosen.beliefs.join(' · ')}
                </div>
              ) : null}
              {w.chosen.sharedBy.length ? (
                <div>
                  {t('rose.hub.sharedBy')}: {w.chosen.sharedBy.join(' · ')}
                </div>
              ) : null}
              {w.chosen.chosenInSession ? <Tag color="blue">{t('rose.hub.enteredInSession')}</Tag> : null}
            </div>
          ) : null}
          {w.chosen && w.plan ? (
            <div data-testid={`rose-hub-plan-${w.strategyId}`} style={{ marginTop: 4 }}>
              <Typography.Text strong>{t('rose.hub.plan')}</Typography.Text>
              {(
                [
                  ['planWeek', w.plan.planWeek],
                  ['planMonth', w.plan.planMonth],
                  ['planYear', w.plan.planYear],
                ] as const
              ).map(([h, items]) =>
                items.length ? (
                  <div key={h}>
                    <b>{t(`rose.hub.${h}`)}</b>
                    {items.map((it, n) => (
                      <div key={n} data-testid={`rose-hub-plan-${h}-${n}`}>
                        {it.what}
                        {it.withWhom ? ` · ${it.withWhom}` : ''}
                        {it.resources ? ` · ${it.resources}` : ''}
                      </div>
                    ))}
                  </div>
                ) : null,
              )}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <>
      <ResultCard
        testId="values-hub-rose"
        role="result"
        collapsible
        title={t('rose.hub.title')}
        actions={
          <>
            {stepButton('describe', t('rose.hub.session.describe'), 'rose-hub-describe-open', null)}
            {stepButton(
              'rate',
              t('rose.hub.session.rate'),
              'rose-hub-rate-open',
              ways.length === 0 ? t('rose.hub.session.describeFirst') : null,
            )}
            {stepButton(
              'choose',
              t('rose.hub.session.choose'),
              'rose-hub-choose-open',
              rated.length === 0 ? t('rose.hub.session.rateFirst') : null,
            )}
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Typography.Text type="secondary" data-testid="rose-hub-credit">
            {t('rose.hub.credit')}
          </Typography.Text>
          {query.isError ? (
            <Alert type="error" showIcon message={t('rose.hub.loadError')} data-testid="rose-hub-load-error" />
          ) : null}
          {query.data && ways.length === 0 ? (
            <Typography.Text type="secondary" data-testid="rose-hub-empty">
              {t('rose.hub.empty')}
            </Typography.Text>
          ) : null}
          {ways.length ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
              {rated.length ? (
                <div style={{ flex: '0 0 auto', maxWidth: '100%', overflowX: 'auto' }}>
          <svg
            aria-label={t('rose.hub.title')}
            data-testid="rose-hub-chart"
            height={ROSE_SIZE}
            role="img"
            width={ROSE_SIZE}
          >
            {[2, 4, 6, 10].map((n) => (
              <circle
                cx={ROSE_CENTRE}
                cy={ROSE_CENTRE}
                fill="none"
                key={n}
                r={(ROSE_RADIUS * n) / 10}
                stroke="#d4c5b4"
              />
            ))}
            <circle
              cx={ROSE_CENTRE}
              cy={ROSE_CENTRE}
              data-testid="rose-hub-ring-8"
              fill="none"
              r={(ROSE_RADIUS * ROSE_ENOUGH) / 10}
              stroke="#333"
              strokeDasharray="4 3"
              strokeWidth={1.5}
            />
            {ROSE_NEEDS.map((need, i) => (
              <g key={need}>
                <line
                  stroke="#d4c5b4"
                  x1={ROSE_CENTRE}
                  x2={ROSE_CENTRE + ROSE_RADIUS * Math.cos(roseAngle(i))}
                  y1={ROSE_CENTRE}
                  y2={ROSE_CENTRE + ROSE_RADIUS * Math.sin(roseAngle(i))}
                />
                <text
                  fill="#666"
                  fontSize={11}
                  textAnchor="middle"
                  x={ROSE_CENTRE + (ROSE_RADIUS + 14) * Math.cos(roseAngle(i))}
                  y={ROSE_CENTRE + (ROSE_RADIUS + 14) * Math.sin(roseAngle(i)) + 4}
                >
                  {i + 1}
                </text>
              </g>
            ))}
            {rated.map((w, i) => (
              <polygon
                data-testid={`rose-hub-polygon-${w.strategyId}`}
                fill={WAY_COLORS[i % WAY_COLORS.length]}
                fillOpacity={0.18}
                key={w.strategyId}
                points={rosePolygon(w.scores as never)}
                stroke={WAY_COLORS[i % WAY_COLORS.length]}
                strokeWidth={2}
              />
            ))}
          </svg>
                </div>
              ) : null}
              <div style={{ flex: '1 1 320px', minWidth: 0 }}>{ways.map((w, i) => wayBlock(w, i))}</div>
            </div>
          ) : null}
          {historyRows.length ? (
            <div data-testid="rose-hub-history">
              <Typography.Text strong>{t('rose.hub.history.title')}</Typography.Text>
              <Table
                columns={[
                  { title: t('rose.hub.history.way'), render: (_: unknown, r) => r.way.name },
                  { title: t('rose.hub.history.date'), render: (_: unknown, r) => fmtDay(r.rating.ratedAt, locale) },
                  {
                    title: t('rose.hub.history.basis'),
                    render: (_: unknown, r) => basisLabel(r.rating.basis),
                  },
                  {
                    title: t('rose.hub.mean'),
                    render: (_: unknown, r) => meanText(r.rating.cappedMean),
                  },
                  {
                    title: t('rose.hub.history.entered'),
                    render: (_: unknown, r) =>
                      r.rating.ratedInSession ? <Tag color="blue">{t('rose.hub.enteredInSession')}</Tag> : null,
                  },
                ]}
                dataSource={historyRows}
                onRow={(r) => ({ 'data-testid': `rose-hub-history-row-${r.way.strategyId}` } as never)}
                pagination={false}
                rowKey="key"
                scroll={{ x: 'max-content' }}
                size="small"
              />
              {historyRows.length < 2 ? (
                <Typography.Text type="secondary" data-testid="rose-hub-history-empty">
                  {t('rose.hub.history.empty')}
                </Typography.Text>
              ) : null}
            </div>
          ) : null}
        </div>
      </ResultCard>

      {drawer === 'describe' ? (
        <DescribeWayDrawer clientId={clientId} clientName={clientName} onClose={closeDrawer} onSaved={saved} />
      ) : null}
      {drawer === 'rate' ? (
        <RateWayDrawer
          clientId={clientId}
          clientName={clientName}
          ways={ways}
          locale={locale}
          onClose={closeDrawer}
          onSaved={saved}
        />
      ) : null}
      {drawer === 'choose' ? (
        <ChooseWayDrawer clientId={clientId} clientName={clientName} rated={rated} onClose={closeDrawer} onSaved={saved} />
      ) : null}
    </>
  );
};
